import { useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard,
  Users,
  FolderOpen,
  Bot,
  DollarSign,
  UserCog,
  FileText,
  ScrollText,
  ChevronRight,
  Menu,
  X,
  LogOut,
  Bell,
  ChevronDown,
  Leaf,
  Settings,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores/auth.store'
import { usePermissions } from '@/hooks/usePermissions'

interface NavItem {
  label: string
  href: string
  icon: React.ReactNode
  adminOnly?: boolean
}

const mainNav: NavItem[] = [
  { label: 'Dashboard',   href: '/dashboard',   icon: <LayoutDashboard className="h-5 w-5" /> },
  { label: 'Produtores',  href: '/producers',   icon: <Users className="h-5 w-5" /> },
  { label: 'Projetos',    href: '/projects',    icon: <FolderOpen className="h-5 w-5" /> },
]

const adminNav: NavItem[] = [
  { label: 'Agentes IA',  href: '/admin/agents',  icon: <Bot className="h-5 w-5" />,     adminOnly: true },
  { label: 'Custos',      href: '/admin/costs',   icon: <DollarSign className="h-5 w-5" />, adminOnly: true },
  { label: 'Usuários',    href: '/admin/users',   icon: <UserCog className="h-5 w-5" />,  adminOnly: true },
  { label: 'Prompts',     href: '/admin/prompts', icon: <FileText className="h-5 w-5" />, adminOnly: true },
  { label: 'Logs',        href: '/admin/logs',    icon: <ScrollText className="h-5 w-5" />, adminOnly: true },
]

function SidebarLink({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  return (
    <NavLink
      to={item.href}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-150',
          'text-agro-100 hover:bg-agro-600 hover:text-white',
          isActive && 'bg-agro-600 text-white shadow-sm',
          collapsed && 'justify-center px-2',
        )
      }
      title={collapsed ? item.label : undefined}
    >
      {item.icon}
      {!collapsed && <span>{item.label}</span>}
    </NavLink>
  )
}

function Breadcrumbs() {
  const location = useLocation()
  const segments = location.pathname.split('/').filter(Boolean)

  const labels: Record<string, string> = {
    dashboard: 'Dashboard',
    producers: 'Produtores',
    projects:  'Projetos',
    admin:     'Admin',
    agents:    'Agentes',
    costs:     'Custos',
    users:     'Usuários',
    prompts:   'Prompts',
    logs:      'Logs',
    new:       'Novo',
    analysis:  'Análise',
    reports:   'Relatórios',
  }

  if (segments.length <= 1) return null

  return (
    <nav className="flex items-center gap-1 text-sm text-gray-500">
      {segments.map((seg, idx) => {
        const isLast = idx === segments.length - 1
        const label = labels[seg] ?? seg
        return (
          <span key={idx} className="flex items-center gap-1">
            {idx > 0 && <ChevronRight className="h-3 w-3" />}
            <span className={cn(isLast ? 'text-gray-900 font-medium' : 'hover:text-gray-700')}>
              {label}
            </span>
          </span>
        )
      })}
    </nav>
  )
}

export function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)

  const { user, logout } = useAuthStore()
  const { isAdmin } = usePermissions()
  const navigate = useNavigate()

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const SidebarContent = ({ collapsed }: { collapsed: boolean }) => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className={cn(
        'flex items-center gap-2 px-4 py-5 border-b border-agro-600',
        collapsed && 'justify-center px-2',
      )}>
        <div className="flex-shrink-0 w-8 h-8 bg-agro-400 rounded-lg flex items-center justify-center">
          <Leaf className="h-5 w-5 text-white" />
        </div>
        {!collapsed && (
          <div>
            <span className="text-white font-bold text-lg leading-none">Agro</span>
            <span className="text-agro-300 font-bold text-lg leading-none">Credit</span>
          </div>
        )}
      </div>

      {/* Main Nav */}
      <div className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {!collapsed && (
          <p className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-agro-400">
            Navegação
          </p>
        )}
        {mainNav.map((item) => (
          <SidebarLink key={item.href} item={item} collapsed={collapsed} />
        ))}

        {isAdmin && (
          <>
            {!collapsed && (
              <p className="px-3 mt-6 mb-2 text-xs font-semibold uppercase tracking-wider text-agro-400">
                Administração
              </p>
            )}
            {collapsed && <div className="my-3 border-t border-agro-600" />}
            {adminNav.map((item) => (
              <SidebarLink key={item.href} item={item} collapsed={collapsed} />
            ))}
          </>
        )}
      </div>

      {/* User info at bottom */}
      <div className={cn(
        'px-3 py-4 border-t border-agro-600',
        collapsed && 'px-2',
      )}>
        {!collapsed ? (
          <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-agro-600">
            <div className="w-8 h-8 rounded-full bg-agro-400 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
              {user?.name?.charAt(0).toUpperCase() ?? 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-agro-300 truncate">{user?.role}</p>
            </div>
          </div>
        ) : (
          <div className="w-8 h-8 rounded-full bg-agro-400 flex items-center justify-center text-white text-sm font-semibold mx-auto">
            {user?.name?.charAt(0).toUpperCase() ?? 'U'}
          </div>
        )}
      </div>
    </div>
  )

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Desktop Sidebar */}
      <aside
        className={cn(
          'hidden md:flex flex-col bg-agro-700 transition-all duration-300 flex-shrink-0',
          sidebarOpen ? 'w-64' : 'w-16',
        )}
      >
        <SidebarContent collapsed={!sidebarOpen} />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-agro-700 flex flex-col z-10">
            <SidebarContent collapsed={false} />
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        {/* Top Header */}
        <header className="flex items-center justify-between h-16 px-4 md:px-6 bg-white border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-4">
            {/* Toggle sidebar on desktop */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="hidden md:flex p-2 rounded-lg hover:bg-gray-100 text-gray-500"
            >
              <Menu className="h-5 w-5" />
            </button>
            {/* Mobile menu */}
            <button
              onClick={() => setMobileOpen(true)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 text-gray-500"
            >
              <Menu className="h-5 w-5" />
            </button>
            <Breadcrumbs />
          </div>

          <div className="flex items-center gap-3">
            {/* Notifications */}
            <button className="relative p-2 rounded-lg hover:bg-gray-100 text-gray-500">
              <Bell className="h-5 w-5" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
            </button>

            {/* User menu */}
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-gray-100 text-gray-700"
              >
                <div className="w-7 h-7 rounded-full bg-agro-500 flex items-center justify-center text-white text-xs font-semibold">
                  {user?.name?.charAt(0).toUpperCase() ?? 'U'}
                </div>
                <span className="hidden sm:block text-sm font-medium">{user?.name}</span>
                <ChevronDown className="h-4 w-4 text-gray-400" />
              </button>

              {userMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setUserMenuOpen(false)}
                  />
                  <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg border border-gray-200 shadow-lg z-20 py-1">
                    <div className="px-3 py-2 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                      <p className="text-xs text-gray-500">{user?.email}</p>
                    </div>
                    <button className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                      <Settings className="h-4 w-4" />
                      Configurações
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      <LogOut className="h-4 w-4" />
                      Sair
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
